import React, { useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Lottie from 'react-lottie';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import landingAnimation from 'assets/animation/landing_animation.json';
import bqHeroAnimation from 'assets/animation/bq_hero_final.mp4';
import Grid from '@mui/material/Grid';
import colors from 'styles/colors';
import bubbleCube from 'assets/images/bubbleCube.png';
import cube from 'assets/images/cube.png';
import solidCube from 'assets/images/solidCube.png';
import './home.css';

const styles = {
  backgroundContainer: {
    border: '1px solid rgba(0, 0, 0, 0.35)',
    borderRadius: '10px',
    position: 'relative',
    minWidth: '900px',
    minHeight: '480px',
    overflow: 'hidden',
    marginTop: '24px',
  },
  backgroundCover: {
    background: 'linear-gradient(163deg, rgba(95,56,116,0.9) 0%, rgba(62,49,119,0.8) 35%, rgba(0,0,0,0.6) 100%)',
    filter: 'progid:DXImageTransform.Microsoft.gradient(startColorstr="#5f3874",endColorstr="#000000",GradientType=1)',
  },
  background: {
    width: 'fit-content',
    height: 'fit-content',
    padding: '50px',
    position: 'absolute',
    top: '0',
  },
  title: {
    fontStyle: 'normal',
    fontWeight: '700',
    fontSize: '72px',
    lineHeight: '97px',
    letterSpacing: '0.03em',
    background: 'radial-gradient(108.82% 190.72% at 61.32% 148.45%, #0060FA 0%, #C175FE 100%)',
    '-webkit-background-clip': 'text',
    '-webkit-text-fill-color': 'transparent',
    backgroundClip: 'text',
    textFillColor: 'transparent',
  },
  subtitle: {
    letterSpacing: '0.03em',
    marginBottom: '40px',
    color: '#FFFFFF',
    opacity: '0.9',
  },
  example: {
    fontSize: '16px',
    lineHeight: '22px',
    color: 'rgba(255, 255, 255, 0.9)',
  },
  import: {
    color: '#CB00DD',
    display: 'inline',
  },
  function: {
    color: '#2579FF',
    display: 'inline',
  },
  order: {
    color: 'rgba(255, 255, 255, 0.25)',
    display: 'inline',
    paddingRight: '10px',
  },
  start: {
    textDecoration: 'none',
    borderRadius: '100px',
    marginTop: '40px',
    height: '56px',
    width: '250px',
  },
  bottomBanner: {
    background: 'linear-gradient(0deg, rgba(2, 64, 163, 0.05), rgba(2, 64, 163, 0.05)), linear-gradient(0deg, rgba(24, 35, 65, 0.12), rgba(24, 35, 65, 0.12)), rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(40px)',
    height: '100px',
    opacity: 0.9,
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    marginBottom: '-2px',
  },
  bannerSection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignContent: 'center',
    cursor: 'pointer',
  },
  video: {
    right: '0px',
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    margin: 'auto',
  },
};

const animationDefaultOptions = {
  loop: true,
  autoplay: true,
  animationData: landingAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const GuestHome = ({ openSignUpDialog }) => {
  const navigate = useNavigate();
  const homeBannerRef = useRef(null);
  const videoRef = useRef(null);

  const calculateBannerHeight = useCallback(() => {
    if (homeBannerRef.current?.parentElement) {
      const availableHeight = homeBannerRef.current.parentElement.parentElement.offsetHeight;
      const hasMultipleChildren = homeBannerRef.current.parentElement.children.length > 1;
      const deduction = hasMultipleChildren ? 120 : 50;
      const videoHeight = Math.max(availableHeight - deduction, 500);
      homeBannerRef.current.style.height = `${videoHeight}px`;
      const containerHeight = availableHeight - deduction < 500 ? videoHeight + 110 : videoHeight + deduction;
      homeBannerRef.current.parentElement.style.height = `${containerHeight}px`;
    }
  }, []);

  useEffect(() => {
    const element = homeBannerRef.current?.parentElement;
    if (!element) return;

    const mutationObserver = new MutationObserver(() => calculateBannerHeight());
    const handleResize = () => calculateBannerHeight();

    mutationObserver.observe(element, { childList: true, subtree: false });
    window.addEventListener('resize', handleResize);
    calculateBannerHeight();

    return () => {
      mutationObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateBannerHeight]);

  return (
    <Box ref={homeBannerRef} id="homeBanner" sx={{ ...styles.backgroundContainer }}>
      <div id="animationCover" />
      <video ref={videoRef} autoPlay loop muted style={styles.video}>
        <source src={bqHeroAnimation} type="video/mp4" />
      </video>
      <Box sx={{ ...styles.background }}>
        <Typography variant="h4" align="left" sx={{ ...styles.subtitle }}>
          The simplest way to run quantum
        </Typography>
        <Lottie
          options={animationDefaultOptions}
          height={100}
          width={500}
          style={{ marginLeft: '0' }}
        />
        <Button sx={{ ...styles.start }} variant="contained" type="primary" onClick={openSignUpDialog}>
          Get Started for Free
        </Button>
      </Box>
      <Box sx={{
        position: 'absolute', bottom: 0, width: '100%', zIndex: 10,
      }}
      >
        <div id="bottomBanner">
          <Grid container sx={styles.bottomBanner} justifyContent="space-around" alignContent="center">
            <Grid item xs={4}>
              <Box sx={styles.bannerSection} onClick={() => navigate('/docs#devices')}>
                <img src={bubbleCube} alt="Simulators and QPUs" />
                <Typography variant="body1" color={colors.text.primary}>Fastest Simulators and QPUs</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={styles.bannerSection} onClick={() => navigate('/docs#getting_started')}>
                <img src={cube} alt="Supports Cirq and Qiskit" />
                <Typography variant="body1" color={colors.text.primary}>Supports Cirq and Qiskit</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={styles.bannerSection} onClick={() => navigate('/docs#default_cpu_simulator')}>
                <img src={solidCube} alt="Simulate qubits" />
                <Typography variant="body1" color={colors.text.primary}>
                  Simulate up to 40 qubits with 3 lines of
                  code
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </div>
      </Box>
    </Box>
  );
};
export default GuestHome;
